<script lang="ts">
	import { authState, logout } from '$lib/stores/auth.svelte';
	import { TRIAL_LIMIT, totalGenerations, coloriageQuota, hasProSubscription } from '$lib/quotas';
	import { billingAlert } from '$lib/billing';
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import {
		Menu as MenuIcon, Sparkles, Euro, BookOpen, LogOut, User, X,
		Crown, ChevronDown, LayoutDashboard, Settings, LayoutGrid, LifeBuoy, CreditCard,
		FileText, CalendarDays, GraduationCap, TrendingUp, ClipboardCheck, Volume2, Wand2
	} from 'lucide-svelte';
	import { MOMENTS } from '$lib/components/nav/toolsCatalog';
	import { trackFunnelEvent } from '$lib/api';

	const { user, subscription } = authState;

	let currentPath = $derived($page.url.pathname);
	let showMobileMenu = $state(false);
	let showProfileMenu = $state(false);
	let showToolsDropdown = $state(false);
	let toolsButtonEl = $state<HTMLButtonElement | undefined>(undefined);
	let toolsDropdownLeft = $state(0);
	let toolsDropdownTop = $state(68);

	function toggleToolsDropdown() {
		if (!showToolsDropdown && toolsButtonEl) {
			const rect = toolsButtonEl.getBoundingClientRect();
			toolsDropdownLeft = rect.left;
			toolsDropdownTop = rect.bottom + 8;
		}
		showToolsDropdown = !showToolsDropdown;
	}

	// ─── Les catégories de l'espace connecté ──────────────────────────────────
	// Les quatre moments de la semaine sont l'entrée ; les outils sont dedans.
	// Même motif de divulgation que le menu « Nos outils » du visiteur ci-dessus
	// (bouton `aria-expanded` + panneau de liens en position fixe) — surtout pas
	// de `role="menubar"` : ce sont des liens de navigation, pas des commandes,
	// et le motif menu impose une gestion de flèches qui dessert les lecteurs
	// d'écran ici.
	let momentOuvert = $state<string | null>(null);
	let boutonsMoment: Record<string, HTMLButtonElement | undefined> = {};
	let panneauLeft = $state(0);
	let panneauTop = $state(68);

	// Un seul événement pour tous les trajets entre outils, quelle que soit la
	// surface d'où part le clic : `source` les distingue (`barre`, `menu-mobile`,
	// et `grille`/`reprendre` côté /app). Le nom `hub_tool_click` est antérieur à
	// cette barre et le suivi hebdomadaire s'appuie dessus : le renommer casserait
	// la série, on garde donc le nom et on élargit sa portée. Sans ça, la mesure
	// des passages d'un outil à l'autre est morte avec la sous-nav.
	function suivreNavigation(outil: string, source: string) {
		trackFunnelEvent('hub_tool_click', { tool: outil, source });
	}

	/** Le moment qui contient l'outil ouvert — l'orientation, faute de sous-nav. */
	let momentCourant = $derived(
		MOMENTS.find((m) => m.outils.some((o) => o.match(currentPath))) ?? null
	);

	function basculeMoment(id: string) {
		if (momentOuvert === id) {
			momentOuvert = null;
			return;
		}
		const bouton = boutonsMoment[id];
		if (bouton) {
			const r = bouton.getBoundingClientRect();
			panneauLeft = r.left;
			panneauTop = r.bottom + 8;
		}
		// Un seul panneau ouvert à la fois, et jamais en même temps qu'un autre
		// menu de la barre.
		showProfileMenu = false;
		showToolsDropdown = false;
		momentOuvert = id;
	}

	function fermeMoment(rendreLeFocus = false) {
		const id = momentOuvert;
		momentOuvert = null;
		if (rendreLeFocus && id) boutonsMoment[id]?.focus();
	}

	$effect(() => {
		if (!momentOuvert) return;
		const auClavier = (e: KeyboardEvent) => {
			if (e.key === 'Escape') fermeMoment(true);
		};
		window.addEventListener('keydown', auClavier);
		return () => window.removeEventListener('keydown', auClavier);
	});

	let isCanceling = $derived($subscription?.subscription_status === 'canceling');
	let hasActiveSubscription = $derived(hasProSubscription($subscription));
	// Un abonnement vivant (même en relance ou résilié en fin de période) a sa
	// page : c'est là que se gèrent carte, factures et résiliation.
	let hasSubscriptionPage = $derived(!!$subscription?.billing);
	let daysRemaining = $derived.by(() => {
		if (!isCanceling || !$subscription?.subscription_end_date) return null;
		const end = new Date($subscription.subscription_end_date);
		const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
		return diff > 0 ? diff : 0;
	});
	// Sous-titre de l'entrée « Mon abonnement » : ce qui attend l'enseignant.
	let subscriptionHint = $derived.by(() => {
		const alerte = billingAlert($subscription?.billing);
		if (alerte?.kind === 'impaye') return 'Paiement en attente';
		if (alerte?.kind === 'carte') return alerte.expired ? 'Carte expirée' : 'Carte à renouveler';
		if (isCanceling && daysRemaining !== null) {
			return `Résilié · accès Pro encore ${daysRemaining} jour${daysRemaining > 1 ? 's' : ''}`;
		}
		return null;
	});

	const tools = [
		{ href: '/generateur', label: 'Fiches séquences', icon: FileText },
		{ href: '/cahier-journal', label: 'Cahier journal', icon: CalendarDays },
		{ href: '/generateur-bulletin', label: 'Générateur de bulletin', icon: GraduationCap },
		{ href: '/generateur-progression-annuelle', label: 'Progressions annuelles', icon: TrendingUp },
		{ href: '/generateur-evaluation', label: 'Évaluations', icon: ClipboardCheck },
		{ href: '/fiches-exercices', label: "Fiches d'exercices", icon: Wand2 },
		{ href: '/guide/plan-de-classe', label: 'Plan de classe', icon: LayoutGrid },
		{ href: '/sonometre', label: 'Sonomètre', icon: Volume2 }
	];

	const cycles = [
		{ href: '/cycle-1-maternelle', label: 'Cycle 1 – Maternelle' },
		{ href: '/cycle-2-cp-ce1-ce2', label: 'Cycle 2 – CP/CE1/CE2' },
		{ href: '/cycle-3-cm1-cm2', label: 'Cycle 3 – CM1/CM2/6e' }
	];

	function getUserInitials(email: string | null): string {
		if (!email) return 'U';
		return email.substring(0, 2).toUpperCase();
	}

	async function handleLogout() {
		showProfileMenu = false;
		showMobileMenu = false;
		const result = await logout();
		if (result.success) goto('/');
	}
</script>

<header class="no-print relative z-30 border-b border-mist bg-paper font-body">
	<div class="container mx-auto flex h-16 items-center px-4">

		<!-- Logo -->
		<!-- « Mon espace » n'est plus un onglet frère des outils : la tuile de
		     marque est le retour au hub pour un connecté, la home pour un visiteur. -->
		<a href={$user ? '/app' : '/'} class="flex shrink-0 items-center gap-2.5">
			<span class="tile-relief size-8 text-lg" aria-hidden="true">§</span>
			<span class="text-base font-bold text-ink">Fiche Séquence</span>
		</a>

		<!-- Desktop nav -->
		<nav class="ml-4 hidden flex-1 items-center md:flex">

			{#if $user}
				<!-- Connecté : une seule navigation, ici. La barre portait cinq
				     entrées qui faisaient doublon ou ne servaient pas au travail,
				     au-dessus d'une sous-navigation pleine ; les deux ont fusionné.
				     Articles et Tarifs vivent dans le menu du compte.
				     Les quatre moments de la semaine sont les catégories ; leurs
				     outils sont dans les panneaux. Un moment qui n'a qu'un outil y
				     mène directement — on n'ouvre pas un menu pour un seul élément,
				     et le basculement suit le nombre d'outils du catalogue, il n'est
				     écrit nulle part en dur. -->
				<div class="mx-2 h-5 w-px shrink-0 bg-mist"></div>
				{#each MOMENTS as moment (moment.id)}
					{@const estCourant = momentCourant?.id === moment.id}
					{#if moment.outils.length === 1}
						<a
							href={moment.outils[0].href}
							onclick={() => suivreNavigation(moment.outils[0].key, 'barre')}
							class="nav-link shrink-0"
							aria-current={estCourant ? 'page' : undefined}
						>
							{moment.court}
						</a>
					{:else}
						<button
							type="button"
							bind:this={boutonsMoment[moment.id]}
							onclick={() => basculeMoment(moment.id)}
							class="nav-link shrink-0"
							aria-expanded={momentOuvert === moment.id}
							aria-controls="panneau-moment"
							aria-current={estCourant ? 'true' : undefined}
						>
							{moment.court}
							<ChevronDown
								class="size-3 transition-transform {momentOuvert === moment.id ? 'rotate-180' : ''}"
							/>
						</button>
					{/if}
				{/each}

				<div class="flex-1"></div>

				<!-- Avatar + profile dropdown -->
				<button
					onclick={() => (showProfileMenu = !showProfileMenu)}
					class="flex items-center gap-2 rounded-full transition-opacity hover:opacity-80"
					aria-label="Menu du compte"
					aria-expanded={showProfileMenu}
					aria-controls="profile-menu"
				>
					<div class="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-warm-surface">
						{#if $user.photoURL}
							<img src={$user.photoURL} alt="avatar" class="size-9 object-cover" />
						{:else}
							<span class="text-sm font-semibold text-ink">{getUserInitials($user.email)}</span>
						{/if}
					</div>
					<ChevronDown class="size-4 text-ash-light transition-transform {showProfileMenu ? 'rotate-180' : ''}" />
				</button>

				{#if showProfileMenu}
					<!-- svelte-ignore a11y_no_static_element_interactions -->
					<div class="fixed inset-0 z-40" onclick={() => (showProfileMenu = false)} onkeydown={(e) => e.key === 'Escape' && (showProfileMenu = false)}></div>
					<div id="profile-menu" class="fixed right-4 top-[68px] z-50 w-80 rounded border border-mist bg-paper p-4 shadow-overlay">
						<!-- User info -->
						<div class="flex items-center gap-3 border-b border-mist pb-3">
							<div class="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-full bg-warm-surface">
								{#if $user.photoURL}
									<img src={$user.photoURL} alt="avatar" class="size-11 object-cover" />
								{:else}
									<span class="text-sm font-semibold text-ink">{getUserInitials($user.email)}</span>
								{/if}
							</div>
							<div class="min-w-0 flex-1">
								<p class="truncate text-sm font-semibold text-ink">{$user.email}</p>
								<p class="flex items-center gap-1 text-xs text-ash">
									{#if $subscription?.subscription_tier === 'pro'}
										<Crown class="size-3 text-marge" /><span class="text-marge font-medium">Pro</span>
									{:else}
										Compte gratuit
									{/if}
								</p>
							</div>
						</div>

						<!-- Usage / upgrade -->
						{#if hasActiveSubscription}
							<div class="mt-3 space-y-2">
								<p class="text-xs font-semibold text-ash">Utilisation ce mois</p>
								<div class="grid grid-cols-2 gap-2">
									<div class="rounded bg-warm-surface p-2.5">
										<p class="text-xs text-ash">Séquences</p>
										<p class="text-sm font-bold text-marge">{$subscription.sequence_generations_count} <span class="text-xs font-normal text-ash">/ illimité</span></p>
									</div>
									<div class="rounded bg-warm-surface p-2.5">
										<p class="text-xs text-ash">Journal IA</p>
										<p class="text-sm font-bold text-ink">Illimité</p>
									</div>
								</div>
							</div>
						{:else}
							<div class="mt-3 rounded border border-mist bg-warm-surface p-3">
								<p class="text-sm font-semibold text-ink">Compte gratuit</p>
								<p class="mt-0.5 text-xs text-ash">{totalGenerations($subscription)}/{TRIAL_LIMIT} générations IA gratuites utilisées</p>
								<!-- Quota à part : les fiches d'exercices ne comptent pas dans les 3 générations. -->
								<p class="mt-0.5 text-xs text-ash">{coloriageQuota($subscription).used}/{coloriageQuota($subscription).offered} fiches d'exercices offertes utilisées</p>
								<a href="/pricing" onclick={() => (showProfileMenu = false)} class="mt-2.5 flex items-center justify-center btn-relief px-4 py-2 text-sm font-semibold">
									Choisir un abonnement
								</a>
							</div>
						{/if}

						<!-- Actions -->
						<div class="mt-3 space-y-0.5 border-t border-mist pt-3">
							<a href="/app" onclick={() => (showProfileMenu = false)} class="flex items-center gap-2 rounded px-3 py-2 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
								<Settings class="size-4" />Mon compte
							</a>
							{#if hasSubscriptionPage}
								<a href="/app/abonnement" onclick={() => (showProfileMenu = false)} class="flex items-start gap-2 rounded px-3 py-2 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
									<CreditCard class="mt-0.5 size-4 shrink-0" />
									<span class="min-w-0">
										<span class="block">Mon abonnement</span>
										{#if subscriptionHint}
											<span class="block text-xs {subscriptionHint.startsWith('Résilié') ? 'text-ash' : 'text-miel'}">{subscriptionHint}</span>
										{/if}
									</span>
								</a>
							{/if}
							<a href="/guide" onclick={() => (showProfileMenu = false)} class="flex items-center gap-2 rounded px-3 py-2 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
								<BookOpen class="size-4" />Articles
							</a>
							<a href="/pricing" onclick={() => (showProfileMenu = false)} class="flex items-center gap-2 rounded px-3 py-2 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
								<Euro class="size-4" />Tarifs
							</a>
							<!-- Seul accès au contact sur les surfaces app : le pied de page y est
							     masqué (voir +layout.svelte, isAppSurface). -->
							<a href="/contact" onclick={() => (showProfileMenu = false)} class="flex items-center gap-2 rounded px-3 py-2 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
								<LifeBuoy class="size-4" />Aide &amp; contact
							</a>
							<button onclick={handleLogout} class="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
								<LogOut class="size-4" />Se déconnecter
							</button>
						</div>

						<button onclick={() => (showProfileMenu = false)} class="absolute right-3 top-3 rounded p-1.5 text-ash-light transition-colors hover:bg-warm-surface hover:text-ink" aria-label="Fermer le menu">
							<X class="size-4" />
						</button>
					</div>
				{/if}

			{:else}
				<!-- Public desktop -->
				<button
					type="button"
					bind:this={toolsButtonEl}
					onclick={toggleToolsDropdown}
					class="nav-link"
					aria-expanded={showToolsDropdown}
					aria-controls="tools-menu"
				>
					<LayoutGrid class="size-4" />Nos outils
					<ChevronDown class="size-3 transition-transform {showToolsDropdown ? 'rotate-180' : ''}" />
				</button>

				<a href="/guide"   class="nav-link {currentPath.startsWith('/guide') ? 'text-encre' : ''}"><BookOpen class="size-4" />Articles</a>
				<a href="/pricing" class="nav-link {currentPath === '/pricing' ? 'text-encre' : ''}"><Euro class="size-4" />Tarifs</a>

				<div class="flex-1"></div>

				<div class="h-5 w-px bg-mist"></div>

				<a href="/login" class="nav-link ml-1">
					<User class="size-4" />Connexion
				</a>
				<a href="/signup" class="ml-1 inline-flex items-center btn-relief px-4 py-2 text-sm font-semibold">
					Créer mon compte gratuit
				</a>
			{/if}
		</nav>

		<!-- Mobile hamburger -->
		<button
			onclick={() => (showMobileMenu = !showMobileMenu)}
			class="ml-auto rounded p-3 text-ash transition-colors hover:bg-warm-surface hover:text-ink md:hidden"
			aria-label="Menu"
			aria-expanded={showMobileMenu}
			aria-controls="mobile-menu"
		>
			{#if showMobileMenu}<X class="size-5" />{:else}<MenuIcon class="size-5" />{/if}
		</button>
	</div>
</header>

<!-- Panneau d'une catégorie (desktop, connecté) -->
{#if momentOuvert}
	{@const moment = MOMENTS.find((m) => m.id === momentOuvert)}
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div
		class="fixed inset-0 z-40 hidden md:block"
		onclick={() => fermeMoment()}
		onkeydown={(e) => e.key === 'Escape' && fermeMoment(true)}
	></div>
	<div
		id="panneau-moment"
		class="fixed z-50 hidden w-80 rounded border border-mist bg-paper p-2 shadow-overlay md:block"
		style:left="{panneauLeft}px"
		style:top="{panneauTop}px"
	>
		{#if moment}
			<p class="px-3 pb-1 pt-1.5 text-xs text-ash">{moment.sous}</p>
			<div class="ligne-marge-panneau">
				{#each moment.outils as outil (outil.key)}
					{@const Icone = outil.icon}
					{@const actif = outil.match(currentPath)}
					<a
						href={outil.href}
						onclick={() => {
							suivreNavigation(outil.key, 'barre');
							fermeMoment();
						}}
						aria-current={actif ? 'page' : undefined}
						class="flex items-start gap-2.5 rounded px-3 py-2.5 text-sm transition-colors hover:bg-warm-surface
							{actif ? 'bg-encre-pale' : ''}"
					>
						<Icone class="mt-0.5 size-4 shrink-0 {actif ? 'text-encre' : 'text-ash'}" />
						<span class="min-w-0">
							<span class="block {actif ? 'font-semibold text-encre' : 'font-medium text-ink'}"
								>{outil.label}</span
							>
							<span class="mt-0.5 block text-xs leading-snug text-ash">{outil.detail}</span>
						</span>
					</a>
				{/each}
			</div>
		{/if}
	</div>
{/if}

<!-- Tools dropdown overlay (desktop, public) -->
{#if showToolsDropdown}
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div class="fixed inset-0 z-40 hidden md:block" onclick={() => (showToolsDropdown = false)} onkeydown={(e) => e.key === 'Escape' && (showToolsDropdown = false)}></div>
	<div
		id="tools-menu"
		class="fixed z-50 hidden w-64 rounded border border-mist bg-paper p-2 shadow-overlay md:block"
		style:left="{toolsDropdownLeft}px"
		style:top="{toolsDropdownTop}px"
	>
		{#each tools as tool (tool.href)}
			{@const Icon = tool.icon}
			<a href={tool.href} onclick={() => (showToolsDropdown = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ink transition-colors hover:bg-warm-surface">
				<Icon class="size-4 text-ash" />{tool.label}
			</a>
		{/each}
		<div class="my-1.5 border-t border-mist"></div>
		<p class="px-3 pb-1 pt-0.5 text-xs font-semibold uppercase tracking-wider text-ash-light">Par cycle</p>
		{#each cycles as cycle (cycle.href)}
			<a href={cycle.href} onclick={() => (showToolsDropdown = false)} class="block rounded px-3 py-2 text-sm text-ink transition-colors hover:bg-warm-surface">
				{cycle.label}
			</a>
		{/each}
	</div>
{/if}

<!-- Mobile menu overlay -->
{#if showMobileMenu}
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div class="fixed inset-0 z-30 md:hidden" onclick={() => (showMobileMenu = false)} onkeydown={(e) => e.key === 'Escape' && (showMobileMenu = false)}></div>
	<div id="mobile-menu" class="fixed left-4 right-4 top-[68px] z-40 rounded border border-mist bg-paper p-4 shadow-overlay md:hidden font-body">
		<nav class="flex flex-col gap-0.5">
			{#if $user}
				<!-- Connected mobile -->
				<div class="flex items-center gap-3 rounded bg-warm-surface px-3 py-2.5 mb-2">
					<div class="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-mist">
						{#if $user.photoURL}
							<img src={$user.photoURL} alt="avatar" class="size-9 object-cover" />
						{:else}
							<span class="text-sm font-semibold text-ink">{getUserInitials($user.email)}</span>
						{/if}
					</div>
					<div class="min-w-0">
						<p class="truncate text-sm font-semibold text-ink">{$user.email}</p>
						<p class="text-xs text-ash">{$subscription?.subscription_tier === 'pro' ? 'Pro' : 'Compte gratuit'}</p>
					</div>
				</div>

				<a href="/app" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ink transition-colors hover:bg-warm-surface">
					<LayoutDashboard class="size-4 text-ash" />Mon espace
				</a>
				<a href="/guide" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ink transition-colors hover:bg-warm-surface">
					<BookOpen class="size-4 text-ash" />Articles
				</a>
				<a href="/pricing" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ink transition-colors hover:bg-warm-surface">
					<Euro class="size-4 text-ash" />Tarifs
				</a>

				<!-- Sans sous-navigation, ce menu est le seul chemin entre outils sur
				     téléphone. Il les porte tous les huit, groupés par les quatre
				     moments de la semaine (cf. HomeSemaine) et séparés par la ligne
				     de marge du cahier — le groupe donne la lecture, la ligne donne
				     le rythme, aucun des deux ne demande de couleur. -->
				{#each MOMENTS as moment (moment.id)}
					<p class="mt-2 px-3 pb-1 pt-1 text-xs font-semibold uppercase tracking-wider text-ash">
						{moment.court}
					</p>
					<div class="ligne-marge-menu">
						{#each moment.outils as outil (outil.key)}
							{@const Icone = outil.icon}
							{@const actif = outil.match(currentPath)}
							<a
								href={outil.href}
								onclick={() => {
									suivreNavigation(outil.key, 'menu-mobile');
									showMobileMenu = false;
								}}
								aria-current={actif ? 'page' : undefined}
								class="flex min-h-[44px] items-center gap-2.5 rounded px-3 py-2.5 text-sm transition-colors hover:bg-warm-surface
									{actif ? 'bg-encre-pale font-semibold text-encre' : 'text-ink'}"
							>
								<Icone class="size-4 shrink-0 {actif ? 'text-encre' : 'text-ash'}" />{outil.label}
							</a>
						{/each}
					</div>
				{/each}

				{#if !hasActiveSubscription}
					<div class="my-1.5 border-t border-mist"></div>
					<div class="rounded bg-marge-pale px-3 py-2 text-sm">
						<p class="font-medium text-marge">Compte gratuit — {totalGenerations($subscription)}/{TRIAL_LIMIT} générations IA utilisées</p>
					</div>
					<a href="/pricing" onclick={() => (showMobileMenu = false)} class="flex items-center justify-center gap-1.5 btn-relief px-4 py-2.5 text-sm font-semibold">
						<Sparkles class="size-4" />Choisir un abonnement
					</a>
				{/if}

				<div class="my-1.5 border-t border-mist"></div>
				<a href="/app" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
					<Settings class="size-4" />Mon compte
				</a>
				{#if hasSubscriptionPage}
					<a href="/app/abonnement" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
						<CreditCard class="size-4" />Mon abonnement
						{#if subscriptionHint}
							<span class="ml-auto text-xs {subscriptionHint.startsWith('Résilié') ? 'text-ash' : 'text-miel'}">{subscriptionHint}</span>
						{/if}
					</a>
				{/if}
				<a href="/contact" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
					<LifeBuoy class="size-4" />Aide &amp; contact
				</a>
				<button onclick={handleLogout} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ash transition-colors hover:bg-warm-surface hover:text-ink">
					<LogOut class="size-4" />Se déconnecter
				</button>

			{:else}
				<!-- Public mobile -->
				<p class="px-3 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wider text-ash-light">Nos outils</p>
				{#each tools as tool (tool.href)}
					{@const Icon = tool.icon}
					<a href={tool.href} onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ink transition-colors hover:bg-warm-surface">
						<Icon class="size-4 text-ash" />{tool.label}
					</a>
				{/each}

				<p class="mt-2 px-3 pb-1.5 pt-1 text-xs font-semibold uppercase tracking-wider text-ash-light">Par cycle</p>
				{#each cycles as cycle (cycle.href)}
					<a href={cycle.href} onclick={() => (showMobileMenu = false)} class="block rounded px-3 py-2.5 text-sm text-ink transition-colors hover:bg-warm-surface">
						{cycle.label}
					</a>
				{/each}

				<div class="my-1.5 border-t border-mist"></div>
				<a href="/pricing" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ink transition-colors hover:bg-warm-surface">
					<Euro class="size-4 text-ash" />Tarifs
				</a>
				<a href="/guide" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded px-3 py-2.5 text-sm text-ink transition-colors hover:bg-warm-surface">
					<BookOpen class="size-4 text-ash" />Articles
				</a>
				<div class="my-1.5 border-t border-mist"></div>
				<a href="/login" onclick={() => (showMobileMenu = false)} class="flex items-center gap-2.5 rounded border border-mist px-4 py-2.5 text-sm font-medium text-ink transition-colors hover:bg-warm-surface">
					<User class="size-4 text-ash" />Connexion
				</a>
				<a href="/signup" onclick={() => (showMobileMenu = false)} class="flex items-center justify-center btn-relief px-4 py-2.5 text-sm font-semibold">
					Créer mon compte gratuit
				</a>
			{/if}
		</nav>
	</div>
{/if}

<style>
	/* La ligne de marge du papier Seyès, reprise de `.page-lignes` : même
	   décalage (2,25 rem), même intensité (35 %), et seule — la réglure
	   horizontale reste à la page. Elle sépare les moments sans les colorer. */
	.ligne-marge-panneau,
	.ligne-marge-menu {
		position: relative;
		padding-left: 0.75rem;
	}
	.ligne-marge-panneau::before,
	.ligne-marge-menu::before {
		content: '';
		position: absolute;
		left: 1.125rem;
		top: 0.125rem;
		bottom: 0.125rem;
		width: 1px;
		background: color-mix(in oklab, var(--color-marge) 35%, transparent);
	}
</style>
